import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Download, ArrowRight, ArrowLeft, CheckCircle2, Layers, ImagePlus, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Seo from "@/components/Seo";
import { SITE_NAME, SITE_ORIGIN } from "@/lib/seo";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "");
const assetUrl = (p) => `${API_BASE}${p}`;
const DRAFT_KEY = "lsh_flyer_draft";

// Testi pubblici "ispirazione" (IT/EN). Le 40 grafiche sono ESEMPI di ispirazione, non modelli da copiare.
const INSPIRATION_TEXT = {
  it: {
    heroCta: "Scegli lo stile che ti ispira",
    title: "Scegli lo stile che ti ispira",
    intro: "Questi flyer sono esempi di ispirazione. Il tuo progetto sarà creato da zero con i tuoi contenuti e il tuo logo, reinterpretando colori, atmosfera e tono dello stile scelto. Il risultato sarà originale e potrà differire dall'esempio.",
    badge: "Ispirazione",
    choose: "Scegli questo stile",
    noResults: "Nessuno stile di ispirazione trovato.",
    countLabel: "stili di ispirazione",
    confirm: "Stile di ispirazione scelto",
    chosenPrefix: "Stile di ispirazione",
  },
  en: {
    heroCta: "Choose the style that inspires you",
    title: "Choose the style that inspires you",
    intro: "These flyers are inspiration examples. Your project will be created from scratch using your content and logo, reinterpreting the colors, mood and tone of the selected style. The final design will be original and may differ from the example.",
    badge: "Inspiration",
    choose: "Choose this style",
    noResults: "No inspiration style found.",
    countLabel: "inspiration styles",
    confirm: "Selected inspiration style",
    chosenPrefix: "Inspiration style",
  },
};
const inspText = (lang) => INSPIRATION_TEXT[lang === "en" ? "en" : "it"];

const OBJECTIVES = [
  { v: "evento", l: "Evento" }, { v: "serata_dj", l: "Serata con DJ" },
  { v: "promozione", l: "Promozione" }, { v: "sconto", l: "Sconto" },
  { v: "nuovo_prodotto", l: "Nuovo prodotto" }, { v: "apertura", l: "Inaugurazione" },
  { v: "menu", l: "Menu" }, { v: "altro", l: "Altro" },
];
const FMT_LABEL = { facebook: "Facebook", instagram: "Instagram", reel: "Reel/Story" };
const FREE_CREATIVE_OPTION = {
  code: "LSH-AI-FREE",
  name: "Lascia fare a noi",
  creative_mode: "free",
  collection: "Libertà creativa",
};

const useDebounced = (v, d = 300) => {
  const [x, setX] = useState(v);
  useEffect(() => { const t = setTimeout(() => setX(v), d); return () => clearTimeout(t); }, [v, d]);
  return x;
};

const ContainImg = ({ src, alt, className = "" }) => (
  <img src={src} alt={alt} loading="lazy" className={`w-full h-full object-contain ${className}`} />
);

// ----------------------------------------------------------------------------
const SocialFlyerCreator = () => {
  const { lang } = useI18n();
  const { user } = useAuth();
  const T = inspText(lang);
  const adminMode = user?.role === "admin";
  const [data, setData] = useState({ items: [], total: 0, pages: 1, collections: [], sectors: [], styles: [] });
  const [q, setQ] = useState("");
  const [collection, setCollection] = useState("all");
  const [sector, setSector] = useState("all");
  const [style, setStyle] = useState("all");
  const [page, setPage] = useState(1);
  const dq = useDebounced(q, 300);
  const [formats, setFormats] = useState(null); // template shown in "3 formati" modal
  const [chosen, setChosen] = useState(null);   // inspiration or free creative choice -> wizard
  const galleryRef = useRef(null);

  useEffect(() => {
    api.get("/flyer/templates", { params: { q: dq, collection, sector, style, page, page_size: 24 } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [dq, collection, sector, style, page]);
  useEffect(() => { setPage(1); }, [dq, collection, sector, style]);

  const goPage = (p) => { setPage(p); requestAnimationFrame(() => galleryRef.current?.scrollIntoView({ block: "start", behavior: "auto" })); };

  // Ritorno dal pagamento: ?session_id=&flyer=<id> -> apri la consegna
  const [delivery, setDelivery] = useState(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const fid = sp.get("flyer"); const sid = sp.get("session_id");
    if (fid && sid) setDelivery({ id: fid, session_id: sid });
  }, []);

  return (
    <div data-testid="flyer-page">
      <Seo title={`Social & Flyer Creator — ${SITE_NAME}`}
        description="Crea il tuo flyer professionale nei formati Facebook, Instagram e Reel/Story. Un solo progetto, tre formati coordinati."
        path="/social-flyer-creator" type="website" />

      {/* HERO */}
      <section className="relative bg-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-50" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-electric/20 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-orange bg-white/5 border border-white/10 px-3 py-1 rounded-full">
            <Layers className="h-3.5 w-3.5" /> Social &amp; Flyer Creator
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading tracking-tight leading-[1.05]">
            Crea il tuo flyer professionale
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/70 max-w-2xl">
            Scegli lo stile, inserisci i dati della tua attività e prepara il tuo progetto nei formati Facebook, Instagram e Reel/Story.
          </p>
          <p className="mt-3 text-brand-orange font-semibold">Un solo progetto, tre formati coordinati.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <Button data-testid="flyer-hero-cta" onClick={() => galleryRef.current?.scrollIntoView({ block: "start", behavior: "smooth" })}
              className="rounded-full h-12 px-7 bg-brand-electric hover:bg-brand-electric/90 text-white text-base gap-2">
              {T.heroCta} <ArrowRight className="h-4 w-4" />
            </Button>
            <Button data-testid="flyer-free-cta" onClick={() => setChosen(FREE_CREATIVE_OPTION)}
              className="rounded-full h-12 px-7 bg-brand-orange hover:bg-brand-orange/90 text-white text-base gap-2">
              Lascia fare a noi
              <span className="rounded-full bg-white/95 text-brand-orange px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide">Consigliato</span>
            </Button>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section ref={galleryRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-12 scroll-mt-20">
        <div className="mb-6 max-w-3xl">
          <h2 data-testid="flyer-gallery-title" className="text-2xl md:text-3xl font-heading font-extrabold text-brand-navy">{T.title}</h2>
          <p data-testid="flyer-gallery-intro" className="mt-2 text-brand-navy/70 text-sm md:text-base">{T.intro}</p>
        </div>
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-navy/40" />
            <Input data-testid="flyer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o codice…" className="pl-9 rounded-full" />
          </div>
          <Select value={collection} onValueChange={setCollection}>
            <SelectTrigger data-testid="flyer-filter-collection" className="w-48 rounded-full"><SelectValue placeholder="Collezione" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le collezioni</SelectItem>{data.collections.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sector} onValueChange={setSector}>
            <SelectTrigger data-testid="flyer-filter-sector" className="w-44 rounded-full"><SelectValue placeholder="Settore" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i settori</SelectItem>{data.sectors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={style} onValueChange={setStyle}>
            <SelectTrigger data-testid="flyer-filter-style" className="w-40 rounded-full"><SelectValue placeholder="Stile" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli stili</SelectItem>{data.styles.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.items.map((tpl) => (
            <div key={tpl.code} data-testid={`flyer-card-${tpl.code}`} className="rounded-2xl border border-brand-navy/10 bg-white overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-brand-light aspect-[4/5] flex items-center justify-center p-2">
                <ContainImg src={assetUrl(tpl.cover_url)} alt={`${T.badge} ${tpl.name}`} />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-heading font-bold text-brand-navy">{tpl.name}</h3>
                  <span data-testid={`flyer-badge-${tpl.code}`} className="text-[10px] font-bold text-brand-orange bg-brand-orange/10 px-2 py-0.5 rounded-full whitespace-nowrap">{T.badge}</span>
                </div>
                <p className="mt-1 text-xs font-mono text-brand-navy/40">{tpl.code} · {tpl.collection}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button data-testid={`flyer-view-${tpl.code}`} onClick={() => setFormats(tpl)} variant="outline" className="rounded-full text-brand-navy border-brand-navy/20 text-sm">Guarda i tre formati</Button>
                  <Button data-testid={`flyer-choose-${tpl.code}`} onClick={() => setChosen(tpl)} className="rounded-full bg-brand-electric text-white text-sm">{T.choose}</Button>
                </div>
              </div>
            </div>
          ))}
          {!data.items.length && <p className="col-span-full text-center text-brand-navy/40 py-10">{T.noResults}</p>}
        </div>

        <div className="flex items-center justify-between mt-8 text-sm text-brand-navy/60">
          <span>{data.total} {T.countLabel} · pagina {page} di {Math.max(1, data.pages)}</span>
          <div className="flex gap-2">
            <Button data-testid="flyer-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => goPage(page - 1)} className="rounded-full">Precedente</Button>
            <Button data-testid="flyer-next" variant="outline" size="sm" disabled={page >= data.pages} onClick={() => goPage(page + 1)} className="rounded-full">Successivo</Button>
          </div>
        </div>
      </section>

      <FormatsModal tpl={formats} onClose={() => setFormats(null)} onChoose={(t) => { setFormats(null); setChosen(t); }} />
      {chosen && <FlyerWizard tpl={chosen} creativeMode={chosen.creative_mode || "guided"} isAdmin={adminMode} onStarted={(id) => { setChosen(null); setDelivery({ id, session_id: "" }); }} onClose={() => setChosen(null)} />}
      {delivery && <DeliveryDialog info={delivery} onClose={() => { setDelivery(null); window.history.replaceState({}, "", "/social-flyer-creator"); }} />}
    </div>
  );
};

// ----------------------------------------------------------------------------
const FLYER_PHASES = [
  "Richiesta acquisita", "Controllo dei dati inseriti", "Analisi del logo e delle ispirazioni",
  "Il grafico AI sta sviluppando la direzione creativa", "Creazione degli elementi visivi",
  "Inserimento di logo, testi e informazioni", "Adattamento nei tre formati",
  "Controllo qualità e coerenza", "Preparazione dei file finali", "Il progetto è pronto",
];

const FlyerProgress = ({ progress, preview, tk }) => {
  const err = progress?.error;
  const pct = typeof progress?.percent === "number" ? progress.percent : (err ? 70 : 5);
  const heartbeatAge = progress?.updated_at ? Date.now() - Date.parse(progress.updated_at) : 0;
  const msg = heartbeatAge > 180000 && !err ? "La lavorazione richiede più tempo del previsto" :
    (progress?.message || (err ? "Generazione interrotta – verifica in corso" : "Preparazione in corso…"));
  const pv = (f) => `${API_BASE}${preview[f]}?draft_token=${tk || ""}`;
  return (
    <div data-testid="flyer-progress" className="px-1">
      <div className="flex items-center justify-between mb-2">
        <p className={`text-sm font-semibold ${err ? "text-amber-600" : "text-brand-navy"}`} data-testid="flyer-progress-message">{msg}</p>
        {!err && <span className="text-xs font-mono text-brand-navy/50" data-testid="flyer-progress-percent">{pct}%</span>}
      </div>
      <div className="h-2.5 rounded-full bg-brand-navy/10 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-700 ease-out ${err ? "bg-amber-400" : "bg-gradient-to-r from-brand-electric to-brand-orange"}`}
             style={{ width: `${Math.max(4, Math.min(100, pct))}%` }} data-testid="flyer-progress-bar" />
      </div>
      {preview && !err && (
        <div data-testid="flyer-progress-preview" className="mt-4">
          <p className="text-[11px] font-semibold text-brand-navy/60 mb-1.5">Anteprima in tempo reale (bozza a bassa risoluzione)</p>
          <div className="grid grid-cols-3 gap-2">
            {["facebook", "instagram", "reel"].map((f) => (
              <div key={f} className="rounded-lg overflow-hidden border border-brand-navy/10 bg-brand-light aspect-[4/5]">
                <img src={pv(f)} alt={`Anteprima ${f}`} className="w-full h-full object-cover" data-testid={`flyer-preview-${f}`}
                     onError={(e) => { e.currentTarget.style.opacity = "0.15"; }} />
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-[11px] text-brand-navy/45 mt-2">
        {err ? "Il nostro team sta verificando il progetto: nessun addebito aggiuntivo. Ti aggiorneremo a breve."
             : "Il grafico AI sta lavorando al tuo progetto. Puoi restare qui o tornare più tardi: l'avanzamento non si perde."}
      </p>
    </div>
  );
};


const DeliveryDialog = ({ info, onClose }) => {
  const [state, setState] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const ref = useRef(null);
  const tkRef = useRef(null);
  useEffect(() => { ref.current && (ref.current.scrollTop = 0); }, [state, proposals]);
  useEffect(() => {
    let tries = 0, stop = false, timer;
    try { const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}"); tkRef.current = d?.draft_token; } catch { /* */ }
    const poll = async () => {
      try {
        const { data } = await api.get(`/flyer/projects/${info.id}/payment/status`, {
          params: { session_id: info.session_id, draft_token: tkRef.current || undefined, _: Date.now() },
          headers: { "Cache-Control": "no-store" },
        });
        setState(data);
        if (data.logo_pending) { loadProposals(); return; }
        if (["delivered", "needs_review", "failed", "interrupted"].includes(data.progress?.state) || data.delivered || data.status === "needs_review") { if (data.delivered) localStorage.removeItem(DRAFT_KEY); return; }
      } catch { /* */ }
      if (!stop) timer = setTimeout(poll, Math.min(15000, 2000 * Math.pow(1.12, tries++)));
    };
    poll();
    return () => { stop = true; clearTimeout(timer); };
  }, [info]);

  const loadProposals = async () => {
    try { const { data } = await api.get(`/flyer/projects/${info.id}/logo/proposals`, { params: { draft_token: tkRef.current || undefined } }); setProposals(data); }
    catch { /* */ }
  };
  const choose = async (index) => {
    setChoosing(true);
    try {
      await api.post(`/flyer/projects/${info.id}/logo/choose`, { index, draft_token: tkRef.current });
      setProposals(null);
      const { data } = await api.get(`/flyer/projects/${info.id}/payment/status`, { params: { session_id: info.session_id, draft_token: tkRef.current || undefined } });
      setState(data); if (data.delivered) localStorage.removeItem(DRAFT_KEY);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile scegliere il logo."); }
    finally { setChoosing(false); }
  };
  const reviseLogos = async () => {
    setChoosing(true);
    try { await api.post(`/flyer/projects/${info.id}/logo/revise`, { draft_token: tkRef.current }); await loadProposals(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Modifica non disponibile."); }
    finally { setChoosing(false); }
  };

  const dl = (path) => { const a = document.createElement("a"); a.href = assetUrl(path); a.target = "_blank"; a.click(); };
  const pUrl = (i) => `${API_BASE}/api/flyer/projects/${info.id}/logo/proposal/${i}?draft_token=${tkRef.current || ""}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent ref={ref} className="max-w-md max-h-[85vh] overflow-auto" data-testid="flyer-delivery">
        <DialogHeader><DialogTitle>Il tuo progetto</DialogTitle></DialogHeader>
        {state && state.logo_pending && proposals ? (
          <div data-testid="flyer-logo-choose" className="py-2">
            <p className="text-sm text-brand-navy/70 mb-3">Scegli la proposta di logo che preferisci:</p>
            <div className="grid grid-cols-3 gap-3">
              {proposals.proposals.map((p) => (
                <button key={p.index} data-testid={`logo-proposal-${p.index}`} disabled={choosing} onClick={() => choose(p.index)}
                  className="rounded-xl border-2 border-brand-navy/10 hover:border-brand-electric bg-[repeating-conic-gradient(#eee_0_25%,#fff_0_50%)] bg-[length:16px_16px] aspect-square flex items-center justify-center p-1">
                  <img src={pUrl(p.index)} alt={`Proposta ${p.index + 1}`} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
            <p className="text-[11px] text-brand-navy/50 mt-3">Il logo è una proposta grafica rapida. Prima di registrarlo o utilizzarlo come marchio, verifica che non sia già protetto da terzi.</p>
            <div className="mt-3 flex justify-between items-center">
              <span className="text-xs text-brand-navy/40">Modifiche: {proposals.revisions_used}/{proposals.revisions_included}</span>
              <Button data-testid="logo-revise" variant="outline" size="sm" disabled={choosing || proposals.revisions_used >= proposals.revisions_included} onClick={reviseLogos} className="rounded-full">Richiedi modifica</Button>
            </div>
          </div>
        ) : !state || !state.paid ? (
          <div className="py-8 text-center text-brand-navy/60"><Loader2 className="h-8 w-8 animate-spin mx-auto text-brand-electric" /><p className="mt-3 font-medium text-brand-navy">Stiamo preparando il tuo progetto.</p><p className="text-sm mt-1">Puoi attendere qui oppure tornare nella tua area personale. La pagina si aggiornerà automaticamente.</p></div>
        ) : state.delivered ? (
          <div data-testid="flyer-delivery-ready" className="text-center py-4">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <p className="mt-3 font-heading font-bold text-brand-navy">Il tuo progetto è pronto!</p>
            <p className="text-sm text-brand-navy/60 mt-1">Scarica i tuoi 3 formati coordinati.</p>
            <div className="mt-5 grid gap-2">
              {["facebook", "instagram", "reel"].map((f) => (
                <Button key={f} data-testid={`delivery-dl-${f}`} onClick={() => dl(state.downloads[f])} variant="outline" className="rounded-full justify-center gap-2"><Download className="h-4 w-4" /> {FMT_LABEL[f]}</Button>
              ))}
              <Button data-testid="delivery-dl-zip" onClick={() => dl(state.downloads.zip)} className="rounded-full bg-brand-electric text-white justify-center gap-2"><Download className="h-4 w-4" /> Scarica tutto (ZIP)</Button>
            </div>
          </div>
        ) : state.status === "needs_review" ? (
          <div data-testid="flyer-delivery-review" className="py-4">
            <div className="text-center">
              <ShieldCheck className="h-12 w-12 text-amber-500 mx-auto" />
              <p className="mt-3 font-heading font-bold text-brand-navy">Bozze pronte · in revisione</p>
            </div>
            {state.review_due_display && (
              <p data-testid="flyer-review-due" className="mt-3 text-center text-sm font-semibold text-brand-navy">Consegna prevista entro: {state.review_due_display}</p>
            )}
            <p data-testid="flyer-review-message" className="text-sm text-brand-navy/60 mt-2 whitespace-pre-line">{state.status_text || state.review_message || "Un grafico umano sta completando i tre formati. Riceverai i file definitivi via email."}</p>
          </div>
        ) : (
          <div data-testid="flyer-delivery-preparing" className="py-6">
            {state.review_due_display && (
              <p data-testid="flyer-preparing-due" className="mb-2 text-center text-sm font-semibold text-brand-navy">Consegna prevista entro: {state.review_due_display}</p>
            )}
            {state.status_text && (
              <p className="mb-4 text-sm text-brand-navy/60 whitespace-pre-line">{state.status_text}</p>
            )}
            <FlyerProgress progress={state.progress} preview={state.preview} tk={tkRef.current} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ----------------------------------------------------------------------------
const FormatsModal = ({ tpl, onClose, onChoose }) => {
  const { lang } = useI18n();
  const T = inspText(lang);
  const ref = useRef(null);
  useEffect(() => { if (tpl && ref.current) ref.current.scrollTop = 0; }, [tpl]);
  return (
    <Dialog open={!!tpl} onOpenChange={(o) => !o && onClose()}>
      <DialogContent ref={ref} className="max-w-4xl max-h-[88vh] overflow-auto" data-testid="flyer-formats-modal">
        {tpl && (<>
          <DialogHeader>
            <DialogTitle>{tpl.name}</DialogTitle>
            <DialogDescription>{tpl.code} · {tpl.collection}{tpl.tags?.length ? ` · ${tpl.tags.join(", ")}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-3 gap-4">
            {["facebook", "instagram", "reel"].map((f) => (
              <div key={f} data-testid={`flyer-format-${f}`}>
                <p className="text-xs font-semibold text-brand-navy/60 mb-1.5">{FMT_LABEL[f]}</p>
                <div className="relative bg-brand-light rounded-xl aspect-[4/5] flex items-center justify-center p-1 border border-brand-navy/10">
                  <ContainImg src={assetUrl(tpl.previews[f])} alt={`Anteprima ${FMT_LABEL[f]} ${tpl.name}`} />
                  <span className="absolute bottom-1.5 left-1.5 text-[9px] bg-brand-navy/70 text-white px-1.5 py-0.5 rounded-full">Anteprima protetta</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Button data-testid="flyer-formats-choose" onClick={() => onChoose(tpl)} className="rounded-full bg-brand-electric text-white">{T.choose}</Button>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};

// ----------------------------------------------------------------------------
const STEP_TITLES = ["Cosa vuoi pubblicizzare?", "La tua attività", "Informazioni da inserire", "Logo e immagini", "Dati di contatto", "Controlla e conferma"];
const TOTAL_STEPS = 6;

const REVIEW_TEXT = {
  it: {
    title: "Controlla e conferma",
    intro: "Dopo il pagamento, l'AI creerà tre bozze grafiche iniziali basate sulle informazioni e sui materiali forniti. Un grafico umano le revisionerà e le completerà prima della consegna dei file definitivi.",
    secInspiration: "Stile di ispirazione", secLogo: "Logo", secContent: "Contenuti del flyer",
    freeChoice: "Lascia fare a noi", freeSection: "Direzione creativa",
    freeIntro: "Nessun modello obbligatorio: il grafico AI decide liberamente stile, grafica e impaginazione usando il brief, il logo e tutti gli indirizzi online forniti.",
    secFormats: "Formati inclusi", secTotal: "Totale",
    note: "Il flyer finale sarà originale e manterrà lo stile, il tono e l'atmosfera dell'ispirazione scelta.",
    freeNote: "Il brief resta la fonte prioritaria. Nome, testi, date, prezzi, contatti e logo non possono essere inventati o modificati.",
    check: "Confermo che testi, date, prezzi e contatti sono corretti.",
    edit: "Modifica i dati", payPrefix: "Conferma e paga",
    adminBtn: "Crea come amministratore", adminBadge: "Modalità amministratore",
    adminForCustomer: "Creazione per conto del cliente",
    adminSection: "Dati cliente (solo interni)", adminNote: "Questi campi restano interni e non compaiono nel flyer.",
    fName: "Nome attività", fHeadline: "Titolo", fDesc: "Descrizione", fDate: "Data", fTime: "Orario",
    fPrice: "Prezzo", fPromo: "Promozione", fCta: "Invito all'azione", fAddress: "Indirizzo", fContacts: "Contatti",
    fLogo: "Logo caricato", fNoLogo: "Nessun logo", fLogoCreate: "Logo da creare (+3,99 €)",
    addonLine: "Add-on logo", none: "—",
  },
  en: {
    title: "Review and confirm",
    intro: "Check that all information is correct. The three formats will be created after confirmation.",
    secInspiration: "Inspiration style", secLogo: "Logo", secContent: "Flyer content",
    freeChoice: "Leave it to us", freeSection: "Creative direction",
    freeIntro: "No mandatory template: the AI designer freely chooses style, graphics and layout using the brief, logo and every online address provided.",
    secFormats: "Included formats", secTotal: "Total",
    note: "The final flyer will be original while preserving the style, tone and atmosphere of the selected inspiration.",
    freeNote: "The brief remains the primary source. Business name, copy, dates, prices, contacts and logo cannot be invented or changed.",
    check: "I confirm that all texts, dates, prices and contact details are correct.",
    edit: "Edit details", payPrefix: "Confirm and pay",
    adminBtn: "Create as administrator", adminBadge: "Administrator mode",
    adminForCustomer: "Creation on behalf of the customer",
    adminSection: "Customer details (internal only)", adminNote: "These fields stay internal and never appear on the flyer.",
    fName: "Business name", fHeadline: "Headline", fDesc: "Description", fDate: "Date", fTime: "Time",
    fPrice: "Price", fPromo: "Promotion", fCta: "Call to action", fAddress: "Address", fContacts: "Contacts",
    fLogo: "Uploaded logo", fNoLogo: "No logo", fLogoCreate: "Logo to create (+€3.99)",
    addonLine: "Logo add-on", none: "—",
  },
};
const reviewText = (lang) => REVIEW_TEXT[lang === "en" ? "en" : "it"];
const euro = (lang, n) => (lang === "en" ? `€${n.toFixed(2)}` : `${n.toFixed(2).replace(".", ",")} €`);

const FlyerWizard = ({ tpl, creativeMode = "guided", onClose, isAdmin = false, onStarted }) => {
  const { user } = useAuth();
  const { lang } = useI18n();
  const T = inspText(lang);
  const RT = reviewText(lang);
  const [step, setStep] = useState(1);
  const [pid, setPid] = useState(null);
  const [pricing, setPricing] = useState({ price: 9.99, logo_addon_price: 3.99 });
  useEffect(() => { api.get("/flyer/pricing").then(({ data }) => setPricing(data)).catch(() => {}); }, []);
  const [token, setToken] = useState(null);
  const [obj, setObj] = useState("");
  const [biz, setBiz] = useState({ business_name: "", sector: "", city: "", address: "", phone: "",
    website: "", facebook: "", instagram: "", tiktok: "" });
  const [content, setContent] = useState({ headline: "", description: "", date: "", time: "", price: "", discount: "", call_to_action: "", extra: "" });
  const [assets, setAssets] = useState([]);
  const [logoMode, setLogoMode] = useState("upload");
  const [logoBrief, setLogoBrief] = useState({ business_name: "", sector: "", style: "", colors: "", symbol: "", text: "", notes: "" });
  const [savedLogos, setSavedLogos] = useState([]);
  const [selectedSavedLogo, setSelectedSavedLogo] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [adminInfo, setAdminInfo] = useState({ name: "", email: "", phone: "", notes: "" });
  const contentRef = useRef(null);

  // load existing draft for this template if present
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.id && d?.draft_token && d?.template_code === tpl.code) {
          api.get(`/flyer/projects/${d.id}`, { params: { draft_token: d.draft_token } }).then(({ data }) => {
            setPid(d.id); setToken(d.draft_token);
            const restored = data.business || {};
            setObj(data.objective_type || "");
            setBiz((b) => ({ ...b, ...restored, facebook: restored.facebook || restored.social || b.facebook || "" }));
            setContent((c) => ({ ...c, ...(data.content || {}) })); setEmail(data.email || ""); setName(data.name || "");
            setAssets((data.assets || []));
          }).catch(() => {});
        }
      }
    } catch { /* ignore */ }
  }, [tpl.code]);

  // Prefill dai dati salvati in "La mia attività" (solo se l'utente è autenticato)
  useEffect(() => {
    if (!user) return;
    api.get("/me/business").then(({ data }) => {
      if (!data) return;
      setBiz((b) => ({
        ...b,
        business_name: b.business_name || data.business_name || "",
        sector: b.sector || data.sector || "", city: b.city || data.city || "",
        address: b.address || data.address || "", phone: b.phone || data.phone || "",
        website: b.website || data.website || "", facebook: b.facebook || data.facebook || data.social || "",
        instagram: b.instagram || data.instagram || "", tiktok: b.tiktok || data.tiktok || "",
      }));
      if (data.public_email) setEmail((e) => e || data.public_email);
      setSavedLogos((data.assets || []).filter((a) => a.kind === "logo"));
      setLogoBrief((lb) => ({ ...lb, business_name: lb.business_name || data.business_name || "", sector: lb.sector || data.sector || "" }));
    }).catch(() => {});
  }, [user]);

  useEffect(() => { contentRef.current?.scrollIntoView({ block: "start", behavior: "auto" }); }, [step]);

  const persist = async (status = "draft") => {
    const payload = { template_code: tpl.code, creative_mode: creativeMode, objective_type: obj, business: biz, content, email, name, status, consent, draft_token: token || undefined };
    if (!pid) {
      const { data } = await api.post("/flyer/projects", payload);
      setPid(data.id); setToken(data.draft_token);
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ id: data.id, draft_token: data.draft_token, template_code: tpl.code }));
      return data;
    }
    const { data } = await api.put(`/flyer/projects/${pid}`, payload);
    return data;
  };

  const saveDraft = async () => {
    setBusy(true);
    try { await persist("draft"); toast.success("Bozza salvata."); }
    catch { toast.error("Impossibile salvare la bozza."); }
    finally { setBusy(false); }
  };

  const next = async () => {
    setBusy(true);
    try { await persist("draft"); setStep((s) => Math.min(TOTAL_STEPS, s + 1)); }
    catch { toast.error("Errore nel salvataggio. Riprova."); }
    finally { setBusy(false); }
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const uploadFile = async (file, kind) => {
    if (!file) return;
    if (!pid) { await persist("draft"); }
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind); if (token) fd.append("draft_token", token);
    try {
      const { data } = await api.post(`/flyer/projects/${pid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // refresh
      const { data: proj } = await api.get(`/flyer/projects/${pid}`, { params: { draft_token: token } });
      setAssets(proj.assets || []);
      toast.success(kind === "logo" ? "Logo caricato." : "Immagine caricata.");
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Caricamento non riuscito."); }
  };
  const removeAsset = async (id) => {
    try { await api.delete(`/flyer/projects/${pid}/asset/${id}`, { params: { draft_token: token } });
      setAssets((a) => a.filter((x) => x.id !== id)); } catch { /* ignore */ }
  };

  const prepConfirm = async () => {
    const saved = await persist("submitted");
    const id = pid || saved?.id;
    const tk = token || saved?.draft_token;
    const savedLogo = savedLogos.find((a) => a.id === selectedSavedLogo) || savedLogos[0];
    if (logoMode === "saved" && savedLogo) {
      try {
        const blob = await api.get(savedLogo.url.replace("/api", ""), { responseType: "blob" });
        const fd = new FormData(); fd.append("file", blob.data, "logo.png"); fd.append("kind", "logo"); if (tk) fd.append("draft_token", tk);
        await api.post(`/flyer/projects/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch { /* ignore */ }
    }
    await api.post(`/flyer/projects/${id}/confirm-brief`, {
      draft_token: tk, logo_addon: logoMode === "create", no_logo: logoMode === "none",
    });
    return { id, tk };
  };

  const confirmAndPay = async () => {
    if (!email) { toast.error("Inserisci un'email per ricevere aggiornamenti."); return; }
    if (!consent) { toast.error("Devi accettare per inviare il progetto."); return; }
    if (!confirmChecked) return;
    setBusy(true);
    try {
      const { id, tk } = await prepConfirm();
      const { data } = await api.post(`/flyer/projects/${id}/checkout`, {
        origin_url: window.location.origin, draft_token: tk,
        logo_addon: logoMode === "create", logo_brief: logoMode === "create" ? logoBrief : undefined,
        no_logo: logoMode === "none",
      });
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      setSubmitted(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Pagamento non avviato. Riprova."); }
    finally { setBusy(false); }
  };

  const confirmAndCreateAdmin = async () => {
    if (!confirmChecked) return;
    setBusy(true);
    try {
      const { id } = await prepConfirm();
      await api.post(`/admin/flyer/projects/${id}/create-on-behalf`, {
        customer_name: adminInfo.name || undefined, customer_email: adminInfo.email || undefined,
        customer_phone: adminInfo.phone || undefined, internal_notes: adminInfo.notes || undefined,
      });
      if (onStarted) onStarted(id); else setSubmitted(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Creazione non avviata. Riprova."); }
    finally { setBusy(false); }
  };

  const showDate = ["evento", "serata_dj", "apertura", "promozione"].includes(obj);
  const showPrice = ["menu", "nuovo_prodotto", "promozione"].includes(obj);
  const showDiscount = ["sconto", "promozione"].includes(obj);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="flyer-wizard">
        <div ref={contentRef} />
        {submitted ? (
          <div data-testid="flyer-submitted" className="text-center py-8">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h3 className="mt-4 text-xl font-heading font-bold text-brand-navy">Il tuo progetto è stato registrato correttamente.</h3>
            <p className="mt-2 text-brand-navy/60 text-sm">{creativeMode === "free" ? RT.freeChoice : `${T.chosenPrefix}: ${tpl.name} · ${tpl.code}`}</p>
            <Button data-testid="flyer-close" onClick={onClose} className="mt-6 rounded-full bg-brand-electric text-white">Chiudi</Button>
          </div>
        ) : (<>
          <DialogHeader>
            <DialogTitle>Passaggio {step} di {TOTAL_STEPS} — {STEP_TITLES[step - 1]}</DialogTitle>
            <DialogDescription>{creativeMode === "free" ? RT.freeIntro : `${T.confirm}: ${tpl.name} (${tpl.code})`}</DialogDescription>
          </DialogHeader>

          {step === 1 && (
            <div className="grid grid-cols-2 gap-2" data-testid="flyer-step-1">
              {OBJECTIVES.map((o) => (
                <button key={o.v} data-testid={`flyer-obj-${o.v}`} onClick={() => setObj(o.v)}
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${obj === o.v ? "border-brand-electric bg-brand-electric/10 text-brand-navy" : "border-brand-navy/15 hover:bg-brand-light text-brand-navy/80"}`}>
                  {o.l}
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="flyer-step-2">
              <div><Label>Nome dell'attività</Label><Input data-testid="flyer-biz-name" value={biz.business_name} onChange={(e) => setBiz({ ...biz, business_name: e.target.value })} className="mt-1" /></div>
              <div><Label>Settore</Label><Input value={biz.sector} onChange={(e) => setBiz({ ...biz, sector: e.target.value })} className="mt-1" /></div>
              <div><Label>Città</Label><Input value={biz.city} onChange={(e) => setBiz({ ...biz, city: e.target.value })} className="mt-1" /></div>
              <div><Label>Indirizzo</Label><Input value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })} className="mt-1" /></div>
              <div><Label>Telefono</Label><Input value={biz.phone} onChange={(e) => setBiz({ ...biz, phone: e.target.value })} className="mt-1" /></div>
              {[
                ["website", "Sito web (facoltativo)"],
                ["facebook", "Profilo o pagina Facebook (facoltativo)"],
                ["instagram", "Profilo Instagram (facoltativo)"],
                ["tiktok", "Profilo TikTok (facoltativo)"],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input data-testid={`flyer-${key}`} value={biz[key] || ""} onChange={(e) => setBiz({ ...biz, [key]: e.target.value })} className="mt-1" placeholder="https://…" />
                  <select data-testid={`${key}-usage`} value={biz[`${key}_usage`] || "research_only"} onChange={(e) => setBiz({ ...biz, [`${key}_usage`]: e.target.value })} className="mt-1 w-full rounded-md border p-2 text-sm">
                    <option value="research_only">Usa soltanto per conoscere l’attività</option>
                    <option value="print">Mostra anche nel flyer</option>
                  </select>
                </div>
              ))}
              <p data-testid="online-sources-note" className="sm:col-span-2 text-xs text-brand-navy/50 rounded-xl bg-brand-light p-3">
                Inserisci soltanto gli indirizzi disponibili. Verranno analizzati tutti e solo quelli compilati; se una fonte richiede login o non è pubblicamente accessibile, il progetto continuerà usando le altre fonti e il brief.
              </p>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-3" data-testid="flyer-step-3">
              <div><Label>Titolo principale</Label><Input data-testid="flyer-headline" value={content.headline} onChange={(e) => setContent({ ...content, headline: e.target.value })} className="mt-1" /></div>
              <div><Label>Breve descrizione</Label><Textarea value={content.description} onChange={(e) => setContent({ ...content, description: e.target.value })} className="mt-1" rows={3} /></div>
              {showDate && <div className="grid grid-cols-2 gap-3">
                <div><Label>Data</Label><Input value={content.date} onChange={(e) => setContent({ ...content, date: e.target.value })} className="mt-1" placeholder="es. 20 luglio" /></div>
                <div><Label>Orario</Label><Input value={content.time} onChange={(e) => setContent({ ...content, time: e.target.value })} className="mt-1" placeholder="es. 22:00" /></div>
              </div>}
              {showPrice && <div><Label>Prezzo (facoltativo)</Label><Input value={content.price} onChange={(e) => setContent({ ...content, price: e.target.value })} className="mt-1" /></div>}
              {showDiscount && <div><Label>Percentuale di sconto (facoltativa)</Label><Input value={content.discount} onChange={(e) => setContent({ ...content, discount: e.target.value })} className="mt-1" placeholder="es. 20%" /></div>}
              <div><Label>Invito all'azione</Label><Input value={content.call_to_action} onChange={(e) => setContent({ ...content, call_to_action: e.target.value })} className="mt-1" placeholder="es. Prenota ora" /></div>
              <div><Label>Informazioni aggiuntive (facoltative)</Label><Textarea value={content.extra} onChange={(e) => setContent({ ...content, extra: e.target.value })} className="mt-1" rows={2} /></div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="flyer-step-4">
              <div className="grid gap-2">
                {savedLogos.length > 0 && (
                  <label data-testid="logo-opt-saved" className={`flex items-center gap-2 rounded-xl border px-4 py-3 cursor-pointer text-sm ${logoMode === "saved" ? "border-brand-electric bg-brand-electric/5" : "border-brand-navy/15"}`}>
                    <input type="radio" name="logomode" checked={logoMode === "saved"} onChange={() => setLogoMode("saved")} />
                    Usa il logo salvato in “La mia attività”
                  </label>
                )}
                <label data-testid="logo-opt-upload" className={`flex items-center gap-2 rounded-xl border px-4 py-3 cursor-pointer text-sm ${logoMode === "upload" ? "border-brand-electric bg-brand-electric/5" : "border-brand-navy/15"}`}>
                  <input type="radio" name="logomode" checked={logoMode === "upload"} onChange={() => setLogoMode("upload")} /> Carica un nuovo logo
                </label>
                <label data-testid="logo-opt-create" className={`flex items-center gap-2 rounded-xl border px-4 py-3 cursor-pointer text-sm ${logoMode === "create" ? "border-brand-orange bg-brand-orange/5" : "border-brand-navy/15"}`}>
                  <input type="radio" name="logomode" checked={logoMode === "create"} onChange={() => setLogoMode("create")} /> Crea un logo rapido <span className="font-bold text-brand-orange">+3,99 €</span>
                </label>
                <label data-testid="logo-opt-none" className={`flex items-center gap-2 rounded-xl border px-4 py-3 cursor-pointer text-sm ${logoMode === "none" ? "border-brand-electric bg-brand-electric/5" : "border-brand-navy/15"}`}>
                  <input type="radio" name="logomode" checked={logoMode === "none"} onChange={() => setLogoMode("none")} /> Continua senza logo
                </label>
              </div>

              {logoMode === "saved" && (
                <div className="grid grid-cols-4 gap-3" data-testid="logo-saved-list">
                  {savedLogos.map((a) => (
                    <button type="button" key={a.id} onClick={() => setSelectedSavedLogo(a.id)} className={`rounded-xl border-2 bg-brand-light aspect-square flex items-center justify-center p-1 ${selectedSavedLogo === a.id ? "border-brand-electric" : "border-brand-navy/10"}`}>
                      <img src={assetUrl(a.url)} alt="logo" className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
              {logoMode === "upload" && (
                <div>
                  <input data-testid="flyer-upload-logo" type="file" accept="image/png,image/jpeg,image/webp" className="block text-sm" onChange={(e) => uploadFile(e.target.files?.[0], "logo")} />
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    {assets.filter((a) => a.kind === "logo").map((a) => (
                      <div key={a.id} className="relative rounded-xl border border-brand-navy/10 bg-brand-light aspect-square flex items-center justify-center p-1">
                        <ContainImg src={assetUrl(a.url) + `?draft_token=${token}`} alt="logo" />
                        <button onClick={() => removeAsset(a.id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1"><Trash2 className="h-3 w-3 text-red-500" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {logoMode === "create" && (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="logo-brief">
                  <div><Label>Nome dell'attività</Label><Input value={logoBrief.business_name} onChange={(e) => setLogoBrief({ ...logoBrief, business_name: e.target.value })} className="mt-1" /></div>
                  <div><Label>Settore</Label><Input value={logoBrief.sector} onChange={(e) => setLogoBrief({ ...logoBrief, sector: e.target.value })} className="mt-1" /></div>
                  <div><Label>Stile desiderato</Label><Input value={logoBrief.style} onChange={(e) => setLogoBrief({ ...logoBrief, style: e.target.value })} className="mt-1" placeholder="es. moderno, elegante" /></div>
                  <div><Label>Colori preferiti</Label><Input value={logoBrief.colors} onChange={(e) => setLogoBrief({ ...logoBrief, colors: e.target.value })} className="mt-1" /></div>
                  <div><Label>Eventuale simbolo</Label><Input value={logoBrief.symbol} onChange={(e) => setLogoBrief({ ...logoBrief, symbol: e.target.value })} className="mt-1" /></div>
                  <div><Label>Testo o iniziali</Label><Input value={logoBrief.text} onChange={(e) => setLogoBrief({ ...logoBrief, text: e.target.value })} className="mt-1" /></div>
                  <div className="sm:col-span-2"><Label>Esempi o indicazioni (facoltativo)</Label><Textarea value={logoBrief.notes} onChange={(e) => setLogoBrief({ ...logoBrief, notes: e.target.value })} className="mt-1" rows={2} /></div>
                  <p className="sm:col-span-2 text-xs text-brand-navy/50">Il logo è una proposta grafica rapida. Prima di registrarlo o utilizzarlo come marchio, verifica che non sia già protetto da terzi.</p>
                </div>
              )}
              <p className="text-xs text-brand-navy/50 flex items-center gap-1"><ImagePlus className="h-3.5 w-3.5" /> Formati ammessi per il caricamento: JPG, PNG, WEBP · max 8MB.</p>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="flyer-step-5">
              <div className={`flex gap-4 ${creativeMode === "free" ? "rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-4" : ""}`}>
                {creativeMode === "free" ? (
                  <div className="w-28 shrink-0 rounded-xl bg-brand-orange text-white flex items-center justify-center text-center px-3 font-heading font-extrabold">
                    Lascia fare a noi
                  </div>
                ) : (
                  <div className="w-28 shrink-0 bg-brand-light rounded-xl aspect-[4/5] flex items-center justify-center p-1 border border-brand-navy/10">
                    <ContainImg src={assetUrl(tpl.cover_url)} alt={tpl.name} />
                  </div>
                )}
                <div className="text-sm">
                  <p className="font-heading font-bold text-brand-navy">{creativeMode === "free" ? RT.freeChoice : tpl.name}</p>
                  <p className="text-xs text-brand-navy/50">{creativeMode === "free" ? "Libertà creativa senza modello obbligatorio" : tpl.code}</p>
                  <p className="mt-2 text-brand-navy/70"><b>Obiettivo:</b> {OBJECTIVES.find((o) => o.v === obj)?.l || "—"}</p>
                  <p className="text-brand-navy/70"><b>Attività:</b> {biz.business_name || "—"} · {biz.city || "—"}</p>
                  <p className="text-brand-navy/70"><b>Titolo:</b> {content.headline || "—"}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Il tuo nome</Label><Input data-testid="flyer-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" /></div>
                <div><Label>Email</Label><Input data-testid="flyer-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" /></div>
              </div>
              <label className="flex items-start gap-2 text-sm text-brand-navy/70">
                <input data-testid="flyer-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
                Acconsento al trattamento dei dati inseriti per la preparazione del progetto.
              </label>
              {assets.length > 0 && <p className="text-xs text-brand-navy/50">{assets.length} file caricati.</p>}
            </div>
          )}

          {step === 6 && (() => {
            const addon = logoMode === "create";
            const total = pricing.price + (addon ? pricing.logo_addon_price : 0);
            const logoAssets = assets.filter((a) => a.kind === "logo");
            const logoImg = logoMode === "saved" ? (savedLogos[0]?.url ? assetUrl(savedLogos[0].url) : null)
              : (logoAssets[0] ? assetUrl(logoAssets[0].url) + `?draft_token=${token}` : null);
            const contactsStr = [biz.address, biz.phone, biz.website, biz.facebook, biz.instagram, biz.tiktok].filter(Boolean).join(" · ");
            const Row = ({ label, value }) => (
              <div className="flex justify-between gap-3 py-1 border-b border-brand-navy/5 last:border-0">
                <span className="text-brand-navy/50 text-xs shrink-0">{label}</span>
                <span className="text-brand-navy text-sm text-right break-words">{value || RT.none}</span>
              </div>
            );
            return (
              <div className="space-y-5" data-testid="flyer-step-6">
                {isAdmin && (
                  <div data-testid="flyer-admin-badge" className="flex flex-wrap items-center gap-2 rounded-xl border border-brand-electric/30 bg-brand-electric/5 px-3 py-2">
                    <span className="text-xs font-bold uppercase tracking-wide bg-brand-electric text-white rounded-full px-2.5 py-1">{RT.adminBadge}</span>
                    <span className="text-sm text-brand-navy/70">{RT.adminForCustomer}</span>
                  </div>
                )}
                <p className="text-sm text-brand-navy/70">{RT.intro}</p>

                <div data-testid="flyer-semiauto-info" className="rounded-xl border border-brand-electric/25 bg-brand-electric/5 p-4 text-sm text-brand-navy/80 space-y-2">
                  <p className="font-heading font-bold text-brand-navy">Come funziona il servizio</p>
                  <p><b>Social &amp; Flyer Creator è un servizio grafico semi-automatico.</b></p>
                  <p>Dopo il pagamento, l'intelligenza artificiale elaborerà le informazioni, i testi, il logo, le immagini e gli eventuali indirizzi online forniti per creare tre bozze grafiche iniziali coordinate: Facebook, Instagram e Reel/Story.</p>
                  <p>Le bozze generate dall'AI non verranno consegnate automaticamente. Un grafico umano controllerà, correggerà e porterà a termine ogni formato, curando impaginazione, leggibilità, proporzioni, utilizzo del logo e coerenza visiva.</p>
                  <p>Soltanto dopo questa revisione riceverai tramite email i tre file definitivi, pronti per essere utilizzati.</p>
                  <p className="text-xs text-brand-navy/60 border-t border-brand-electric/20 pt-2">Il servizio online riceve richieste 24 ore su 24. La revisione e il completamento da parte del grafico umano vengono effettuati dal lunedì al sabato, dalle ore 09:00 alle ore 19:00. Le richieste inviate fuori da questi orari saranno regolarmente acquisite e le bozze potranno essere generate automaticamente dall'AI. L'intervento del grafico umano inizierà nella prima fascia di servizio utile.</p>
                  <p className="text-xs text-brand-navy/60">I tre file definitivi saranno consegnati entro tre ore lavorative. Le ore lavorative vengono conteggiate dal lunedì al sabato, dalle ore 09:00 alle ore 19:00. Le richieste inviate fuori da questa fascia saranno prese in carico nel primo orario utile.</p>
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-navy/40 mb-2">{creativeMode === "free" ? RT.freeSection : RT.secInspiration}</p>
                  {creativeMode === "free" ? (
                    <div data-testid="review-free-mode" className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-3 text-sm">
                      <p className="font-heading font-bold text-brand-orange">{RT.freeChoice} · Consigliato</p>
                      <p className="mt-1 text-brand-navy/65">{RT.freeIntro}</p>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <div className="w-24 shrink-0 bg-brand-light rounded-xl aspect-[4/5] flex items-center justify-center p-1 border border-brand-navy/10">
                        <ContainImg src={assetUrl(tpl.cover_url)} alt={tpl.name} />
                      </div>
                      <div className="text-sm">
                        <p className="font-heading font-bold text-brand-navy">{tpl.name}</p>
                        <p className="font-mono text-xs text-brand-navy/40">{tpl.code}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-navy/40 mb-2">{RT.secLogo}</p>
                  <div className="flex items-center gap-3" data-testid="review-logo">
                    {logoImg
                      ? <div className="w-16 h-16 rounded-xl border border-brand-navy/10 bg-brand-light flex items-center justify-center p-1"><img src={logoImg} alt="logo" className="w-full h-full object-contain" /></div>
                      : null}
                    <span className="text-sm text-brand-navy/70">{logoMode === "create" ? RT.fLogoCreate : logoMode === "none" ? RT.fNoLogo : RT.fLogo}</span>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-navy/40 mb-2">{RT.secContent}</p>
                  <div data-testid="review-content" className="rounded-xl border border-brand-navy/10 bg-white p-3">
                    <Row label={RT.fName} value={biz.business_name} />
                    <Row label={RT.fHeadline} value={content.headline} />
                    <Row label={RT.fDesc} value={content.description} />
                    <Row label={RT.fDate} value={content.date} />
                    <Row label={RT.fTime} value={content.time} />
                    <Row label={RT.fPrice} value={content.price} />
                    <Row label={RT.fPromo} value={content.discount} />
                    <Row label={RT.fCta} value={content.call_to_action} />
                    <Row label={RT.fAddress} value={biz.address} />
                    <Row label={RT.fContacts} value={contactsStr} />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-navy/40 mb-2">{RT.secFormats}</p>
                  <div className="flex gap-2 flex-wrap">
                    {["facebook", "instagram", "reel"].map((f) => (
                      <span key={f} className="text-xs rounded-full bg-brand-electric/10 text-brand-navy px-3 py-1">{FMT_LABEL[f]}</span>
                    ))}
                  </div>
                </div>

                <div data-testid="review-total">
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-navy/40 mb-2">{RT.secTotal}</p>
                  <div className="rounded-xl border border-brand-navy/10 bg-white p-3 text-sm">
                    <div className="flex justify-between py-1"><span className="text-brand-navy/60">Flyer (3 formati)</span><span data-testid="review-price-flyer" className="text-brand-navy">{euro(lang, pricing.price)}</span></div>
                    {addon && <div className="flex justify-between py-1"><span className="text-brand-navy/60">{RT.addonLine}</span><span data-testid="review-price-addon" className="text-brand-navy">{euro(lang, pricing.logo_addon_price)}</span></div>}
                    <div className="flex justify-between pt-2 mt-1 border-t border-brand-navy/10 font-bold"><span className="text-brand-navy">{RT.secTotal}</span><span data-testid="review-price-total" className="text-brand-navy">{euro(lang, total)}</span></div>
                  </div>
                </div>

                {isAdmin && (
                  <div data-testid="review-admin-fields">
                    <p className="text-xs font-bold uppercase tracking-wide text-brand-navy/40 mb-2">{RT.adminSection}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div><Label>Nome cliente</Label><Input data-testid="admin-customer-name" value={adminInfo.name} onChange={(e) => setAdminInfo({ ...adminInfo, name: e.target.value })} className="mt-1" /></div>
                      <div><Label>Email cliente</Label><Input data-testid="admin-customer-email" value={adminInfo.email} onChange={(e) => setAdminInfo({ ...adminInfo, email: e.target.value })} className="mt-1" /></div>
                      <div><Label>Telefono cliente</Label><Input data-testid="admin-customer-phone" value={adminInfo.phone} onChange={(e) => setAdminInfo({ ...adminInfo, phone: e.target.value })} className="mt-1" /></div>
                      <div><Label>Note interne / consegna</Label><Input data-testid="admin-internal-notes" value={adminInfo.notes} onChange={(e) => setAdminInfo({ ...adminInfo, notes: e.target.value })} className="mt-1" /></div>
                    </div>
                    <p className="text-xs text-brand-navy/45 mt-1">{RT.adminNote}</p>
                  </div>
                )}

                <p className="text-xs text-brand-navy/55 bg-brand-light rounded-xl p-3">{creativeMode === "free" ? RT.freeNote : RT.note}</p>

                <label className="flex items-start gap-2 text-sm text-brand-navy/80">
                  <input data-testid="flyer-confirm-checkbox" type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} className="mt-1" />
                  {RT.check}
                </label>

                {!isAdmin && (
                  <p data-testid="flyer-pay-summary" className="text-xs text-brand-navy/70 bg-brand-orange/5 border border-brand-orange/20 rounded-xl p-3">
                    Con il pagamento avvierai un servizio grafico semi-automatico: l'AI creerà tre bozze iniziali sulla base delle informazioni fornite e un grafico umano le revisionerà e le completerà prima della consegna.
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button data-testid="flyer-confirm-edit" onClick={back} variant="outline" disabled={busy} className="rounded-full">{RT.edit}</Button>
                  {isAdmin
                    ? <Button data-testid="flyer-confirm-admin" onClick={confirmAndCreateAdmin} disabled={busy || !confirmChecked} className="rounded-full bg-brand-electric text-white gap-2 flex-1"><ShieldCheck className="h-4 w-4" /> {RT.adminBtn}</Button>
                    : <Button data-testid="flyer-confirm-pay" onClick={confirmAndPay} disabled={busy || !confirmChecked} className="rounded-full bg-brand-orange text-white gap-2 flex-1"><ShieldCheck className="h-4 w-4" /> {RT.payPrefix} {euro(lang, total)}</Button>}
                </div>
              </div>
            );
          })()}

          <div className="mt-6 flex items-center justify-between gap-2 border-t border-brand-navy/10 pt-4">
            <Button data-testid="flyer-back" onClick={back} variant="outline" disabled={step === 1 || busy} className="rounded-full gap-1"><ArrowLeft className="h-4 w-4" /> Indietro</Button>
            <div className="flex gap-2">
              <Button data-testid="flyer-save-draft" onClick={saveDraft} variant="outline" disabled={busy} className="rounded-full">Salva bozza</Button>
              {step < TOTAL_STEPS
                ? <Button data-testid="flyer-continue" onClick={next} disabled={busy || (step === 1 && !obj)} className="rounded-full bg-brand-electric text-white gap-1">Continua <ArrowRight className="h-4 w-4" /></Button>
                : null}
            </div>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};

export default SocialFlyerCreator;
